// Adapter: legacy (v3-like) site preferences payloads -> SSAPI v4 preferences[]

const PROGRAM_CODE_BY_KEY = {
  k5Digital: 'K5P',
  mspDigital: 'MSP',
  highSchool: 'HSP',
}

const ACCESS_CODE_BY_KEY = {
  k5Digital: 'k5SelDigital',
  mspDigital: 'mspDigital',
  highSchool: 'highSchool',
}

const EXCLUDED_PREFERENCE_KEYS = ['AreUsersAdded']

const createWarnCollector = funcName => {
  const counts = new Map()
  const add = field => counts.set(field, (counts.get(field) || 0) + 1)
  const flush = () => {
    if (!counts.size) return
    const summary = Array.from(counts.entries())
      .map(([f, c]) => `${f} (count=${c})`)
      .join(', ')
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[adapters] ${funcName}: ${summary}`)
    }
  }
  return { add, flush }
}

const REVERSE_NAME_MAP = {
  mindYeti: 'MindYeti',
  tiAssessment: 'TIAssessment',
  bullyingHarassmentUnitEnabled: 'BullyingHarassmentUnitEnabled',
  regions: 'Regions',
  studentActivities: 'StudentActivities',
  programIsEnabled: 'ProgramIsEnabled',
  implementationType: 'ImplementationType',
  implementationLevel: 'ImplementationLevel',
  programTrack: 'ProgramTrack', // SiteUser specific in v4, but safe as a general reverse map
}

const capitalizeFirst = name => {
  if (typeof name !== 'string' || name.length === 0) return name
  return name.charAt(0).toUpperCase() + name.slice(1)
}

const reverseMapPreferenceName = key =>
  REVERSE_NAME_MAP[key] || capitalizeFirst(key)

const inferValueType = (name, value) => {
  // Arrays and dates first
  if (Array.isArray(value)) return 'Stringarray'
  if (value instanceof Date) return 'Date'

  const n = (name || '').toLowerCase()
  const nameSuggestsNumber =
    n.includes('level') ||
    n.endsWith('value') ||
    n.endsWith('version') ||
    n === 'periods'
  const nameSuggestsBoolean =
    n.startsWith('is') ||
    n.startsWith('has') ||
    n.endsWith('enabled') ||
    n.endsWith('prepared') ||
    n.endsWith('hosted') ||
    n.endsWith('sent') ||
    n.endsWith('led')
  const nameSuggestsDate = n.endsWith('date') || n === 'programstartdate'
  const nameSuggestsArray = n === 'regions'

  if (nameSuggestsArray) return 'Stringarray'
  if (nameSuggestsDate) return 'Date'

  const t = typeof value
  if (t === 'number' && Number.isFinite(value)) return 'Number'
  if (t === 'boolean') return 'Boolean'

  if (t === 'string') {
    if (nameSuggestsNumber) return 'Number'
    if (nameSuggestsBoolean) return 'Boolean'
    return 'String'
  }

  if (value == null) {
    if (nameSuggestsNumber) return 'Number'
    if (nameSuggestsBoolean) return 'Boolean'
    return 'String'
  }

  return 'String'
}

const normalizeValueForType = (value, valueType) => {
  if (value == null) return null
  switch (valueType) {
    case 'Date':
      return value instanceof Date ? value.toISOString() : value
    case 'Number':
      return typeof value === 'string' ? Number(value) : value
    case 'Boolean':
      return !!value
    case 'Stringarray':
      return Array.isArray(value) ? value.map(String) : [String(value)]
    case 'String':
    default:
      return String(value)
  }
}

const buildEntriesFromObject = ({
  obj,
  assignmentType,
  programCode,
  accessCode,
}) => {
  if (!obj || typeof obj !== 'object') return []
  const merged = new Map()

  Object.entries(obj).forEach(([key, value]) => {
    if (
      EXCLUDED_PREFERENCE_KEYS.some(
        excludedKey => key.toLowerCase() === excludedKey.toLowerCase(),
      )
    )
      return
    if (key.endsWith('IsCompleted')) {
      const base = key.slice(0, -'IsCompleted'.length)
      const entry = merged.get(base) || {}
      entry.isCompleted = !!value
      merged.set(base, entry)
      return
    }
    const entry = merged.get(key) || {}
    entry.value = value
    merged.set(key, entry)
  })

  const results = []
  merged.forEach((entry, baseKey) => {
    const name = reverseMapPreferenceName(baseKey)
    const valueType = inferValueType(name, entry.value)
    const obj = {
      assignmentType,
      name,
      isCompleted: !!entry.isCompleted,
      accessCode,
      programCode,
      valueType,
      value: normalizeValueForType(
        entry.value === undefined ? null : entry.value,
        valueType,
      ),
    }
    results.push(obj)
  })
  return results
}

const buildPrefIndexFromSites = referenceSites => {
  const index = new Map()
  if (!Array.isArray(referenceSites)) return index
  for (const site of referenceSites) {
    const prefs = site && site.preferences
    if (!Array.isArray(prefs)) continue
    for (const pref of prefs) {
      if (!pref) continue
      const key = `${pref.assignmentType}|${pref.programCode || ''}|${
        pref.name
      }`
      if (!index.has(key)) {
        index.set(key, {
          preferenceTypeId: pref.preferenceTypeId,
          programId: pref.programId ?? null,
        })
      }
    }
  }
  return index
}

const attachTypeAndProgramIds = ({ items, referenceSites, warnFuncName }) => {
  const wc = createWarnCollector(warnFuncName)
  const idx = buildPrefIndexFromSites(referenceSites)
  const mapped = items.map(p => {
    const key = `${p.assignmentType}|${p.programCode || ''}|${p.name}`
    const found = idx.get(key)
    if (!found) {
      wc.add(`missing preferenceTypeId/programId for key="${key}"`)
      return p
    }
    return {
      ...p,
      preferenceTypeId: found.preferenceTypeId,
      programId: found.programId,
    }
  })
  wc.flush()
  return mapped
}

export const adaptLegacySitePreferencesToV4 = (
  legacy,
  { referenceSites } = {},
) => {
  if (!legacy || typeof legacy !== 'object') return []
  // If already array in v4 shape, pass through
  if (Array.isArray(legacy)) return legacy

  const results = []

  Object.entries(legacy).forEach(([programKey, prefs]) => {
    if (!prefs || typeof prefs !== 'object') return
    const programCode = PROGRAM_CODE_BY_KEY[programKey]
    if (!programCode) return
    const accessCode = ACCESS_CODE_BY_KEY[programKey]
    // Use top-level keys; ignore userProgramPreferences here
    results.push(
      ...buildEntriesFromObject({
        obj: prefs,
        assignmentType: 'Site',
        programCode,
        accessCode,
      }),
    )
  })
  const withIds = attachTypeAndProgramIds({
    items: results,
    referenceSites,
    warnFuncName: 'adaptLegacySitePreferencesToV4',
  })
  return withIds
}

export const adaptLegacySiteUserPreferencesToV4 = (
  legacy,
  { referenceSites } = {},
) => {
  if (!legacy || typeof legacy !== 'object') return []
  // If already array in v4 shape, pass through
  if (Array.isArray(legacy)) return legacy

  const results = []

  Object.entries(legacy).forEach(([programKey, prefs]) => {
    const programCode = PROGRAM_CODE_BY_KEY[programKey]
    if (!programCode) return
    const userPrefs = prefs && prefs.userProgramPreferences
    if (!userPrefs || typeof userPrefs !== 'object') return
    const accessCode = ACCESS_CODE_BY_KEY[programKey]

    results.push(
      ...buildEntriesFromObject({
        obj: userPrefs,
        assignmentType: 'SiteUser',
        programCode,
        accessCode,
      }),
    )
  })
  const withIds = attachTypeAndProgramIds({
    items: results,
    referenceSites,
    warnFuncName: 'adaptLegacySiteUserPreferencesToV4',
  })
  return withIds
}

export default {
  adaptLegacySitePreferencesToV4,
  adaptLegacySiteUserPreferencesToV4,
}
